// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { User, UserLogonState, UserTypes } from '@interfaces/user.interfaces';
import { ErrorService } from '@services/error.service';
import { NavService } from '@services/nav.service';
import { SuccessModalService } from '@services/success-modal.service';
import { UserService } from '@services/user.service';
import { debounceTime, distinctUntilChanged, map, Observable, switchMap, take, takeWhile, tap } from 'rxjs';
import { PermissionsService } from '../../services/permissions.service';
import { AuthService } from '../services/auth.service';
import { LoginService } from '../services/login.service';
import { emailPattern } from 'src/app/utils/form-utils';
import { MatDialog } from '@angular/material/dialog';
import { NetworkTestModalComponent } from 'src/app/modals/network-test-modal/network-test-modal.component';
import { EnrollmentService } from '@services/enrollment.service';
import { AppEventEx } from '@interfaces/interfaces';
import { SettingsService } from '@services/settings.service';
import { AuthenticationStatus } from '@interfaces/auth.interfaces';

@Component({
    selector: 'app-login-form',
    templateUrl: './login-form.component.html',
    styleUrls: ['./login-form.component.scss'],
})
export class LoginFormComponent implements OnInit, OnDestroy {
    public isComponentAlive = true;
    public isLoading = false;
    public isLoginShowing = true;
    public isVerifyShowing = false;
    public logInForm: FormGroup;
    protected phone: string = null;
    public viewPassword = false;
    protected requestAccessJacketId: number = null;
    private isDealershipTerminated = false;
    private userHasOtherAvailableDealerships = false;

    constructor(
        private readonly dialog: MatDialog,
        private readonly router: Router,
        private readonly authService: AuthService,
        private readonly enrollmentService: EnrollmentService,
        private readonly errorService: ErrorService,
        private readonly loginService: LoginService,
        private readonly navService: NavService,
        private readonly permissionsService: PermissionsService,
        private readonly settingsService: SettingsService,
        private readonly successModalService: SuccessModalService,
        private readonly userService: UserService
    ) {}

    ngOnInit(): void {
        this.navService.setTitle('Login');

        this.logInForm = new FormGroup({
            email: new FormControl('', {
                validators: [Validators.required, Validators.pattern(emailPattern)],
            }),
            password: new FormControl('', {
                validators: [Validators.required],
            }),
        });

        this.logInForm.controls.email.valueChanges
            .pipe(
                takeWhile(() => this.isComponentAlive),
                debounceTime(500),
                distinctUntilChanged()
            )
            .subscribe((value) => {
                value.match(/\s+$/) && this.logInForm.controls.email.setValue(value.trim());
            });

        const redirectUrl = this.authService.redirectUrl;
        if (redirectUrl) {
            const regex = /\/dashboard\/documents\/(\d+)/i;
            const match = redirectUrl.match(regex);
            if (match && match.length > 1) {
                // this is a document jacket that the user is trying to login to.
                const jacketId = parseInt(match[1]);

                // do an enrollment check for the jacketId...
                this.enrollmentService.getEnrollmentInfoFromJacketId$(jacketId).subscribe((enrollments) => {
                    // no enrollments == not a real dealer == not a real jacketId == do nothing special, and leave them on the login form
                    if (enrollments) {
                        if (enrollments.isActive) {
                            // let the form know to display the "request access" link
                            this.requestAccessJacketId = jacketId;
                            this.settingsService.setOemCode(enrollments.oemCode);
                        } else {
                            const page = 'verification';
                            // ...send this user to the SSO flow so they can enroll themselves.
                            this.router.navigateByUrl(`/login/firstUserLogging/entrypoint?page=${page}&jacketId=${encodeURIComponent(jacketId)}`);
                        }
                    }
                });
            }
        }
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }

    authenticateFailure(): void {
        this.isLoading = false;
        this.authService.signOut();
    }

    authenticateSuccess(user: User): void {
        this.userService.setUser(user);
        //only dealer users see termination messages
        if (UserTypes.Dealer == user.userType && this.isDealershipTerminated) {
            // this user's dealership is past it's TurnOffAccessDate.

            if (this.userHasOtherAvailableDealerships) {
                // this user has other dealerships they can look at.
                this.postAuthenticationSuccess(user).subscribe();
            } else {
                // they've successfully "logged on", have passed mfa, etc... but their dealership is terminated...
                this.userService.updateLastLogon$(user, UserLogonState.DealerNotEnrolled).subscribe();

                // show the user a message, and leave them on the login page to contemplate their life choices.
                this.loginService.showTerminatedDealershipDialog();

                this.isLoading = this.isVerifyShowing = false;
                this.isLoginShowing = true;
            }
        }

        this.postAuthenticationSuccess(user).subscribe();
    }

    postAuthenticationSuccess(user: User): Observable<void> {
        //update the last logon date here now that authenticated
        return this.userService.updateLastLogon$(user, UserLogonState.Success).pipe(
            tap(() => this.loginService.logLoginState(user.userId, user.logonId, UserLogonState.Success)),
            switchMap(() => {
                return this.userService.getUser$().pipe(
                    take(1),
                    map((updatedUser) => {
                        // if any user logs on for the first time and needs to go through the set up process
                        if (updatedUser.sendToWelcomeFlow) {
                            this.router.navigate(['/welcome']);
                        }
                        // if any user needs to agree to a system doc (ie, privacy policy, terms of service)
                        else if (updatedUser.needToAcceptSystemDocument) {
                            this.router.navigate(['systemDocument']);
                        }
                        // if you lose your token and are logged out, or try to access a url in the app, we redirect you to the route you were trying to access
                        else if (this.authService.redirectUrl) {
                            this.router.navigate([this.authService.redirectUrl]);
                            this.authService.redirectUrl = null;
                        }
                        // customer goes to deals page
                        else if (updatedUser.userType === UserTypes.Customer) {
                            this.router.navigate(['/main', 'vehicles']);
                        }
                        // dealer check if they have a home page set and send them there, or send to deals
                        else {
                            this.router.navigate(this.permissionsService.routeToDealerHomePage());
                        }
                    })
                );
            })
        );
    }

    authenticateTokenFailure(): void {
        this.authenticateFailure();
    }

    authenticateTokenSuccess(user: User): void {
        this.authenticateSuccess(user);
    }

    testNetwork() {
        this.dialog.open(NetworkTestModalComponent, {
            width: '700px',
            closeOnNavigation: false,
            data: {},
        });
    }

    loginUser(email: string, password: string): void {
        this.loginService.authenticateUser(
            email,
            password,
            null,
            (response) => {
                const user = response.user;
                if (!user.active) {
                    this.isLoading = false;
                    this.errorService.showError({
                        statusCode: 500,
                        message: 'Your access has been temporarily disabled. Contact your system administrator to reactivate your user account.',
                        title: 'User Disabled',
                    });
                    return;
                }

                // dealership termination info is used in authenticateSuccess() to switch dealerships if needed
                this.isDealershipTerminated = response.isDealershipTerminated;
                this.userHasOtherAvailableDealerships = response.hasOtherDealershipsAvailable;
                this.authenticateSuccess(user);
            },
            () => {
                this.isLoading = false;
                this.errorService.showError({
                    statusCode: 500,
                    message:
                        'Please check your email and / or password to ensure you have entered them correctly. If you have forgotten your password, close this window and click on Reset Password to create a new one. If you cannot remember your email address, contact the dealership.',
                    title: 'Login Attempt Unsuccessful',
                });
                this.authenticateFailure();
            },
            (state) => this.mfaCallback(state.phoneValidation)
        );
    }

    // login method
    logInSubmit(): void {
        if (this.logInForm.status !== 'VALID') {
            return;
        }

        this.isLoading = true;
        const { email, password } = this.logInForm.value;
        this.loginUser(email, password);
    }

    logonChange() {
        this.isLoginShowing = true;
        this.isVerifyShowing = false;
        this.logInForm.reset();
    }

    mfaCallback(phone: string): void {
        this.isLoginShowing = false;
        this.isVerifyShowing = true;
        this.phone = phone;
        this.isLoading = false;
    }

    public passwordVisible(): void {
        this.viewPassword = !this.viewPassword;
    }

    verifyChange(event: AppEventEx<AuthenticationStatus>) {
        if (event.context == 'success') {
            this.authenticateTokenSuccess(event.data.user);
        } else if (event.context == 'newToken') {
            this.successModalService.showSuccess({
                title: 'Verification Code Sent',
                content:
                    'A new verification code has been sent. Please close this window and enter the new code. If you do not receive the code within 5 minutes, click “Request New Code” to start over. Note: the verification code is only valid for 5 minutes.',
            });
            const { email, password } = this.logInForm.value;
            const user = this.userService.user;
            this.loginService.authenticateUser(
                email,
                password,
                null,
                () => this.authenticateSuccess(user),
                () => this.authenticateFailure(),
                (state) => this.mfaCallback(state.phoneValidation)
            );
        } else {
            this.authenticateTokenFailure();
        }
    }
}
